import { Helmet } from "react-helmet-async";
import { shopPath } from "../lib/paths";

const SITE = "Flowerlive";
const BASE = typeof window !== "undefined" ? window.location.origin : "https://flowerlive.it";

export const Seo = ({ title, description, path = "/", image = "/icons/icon-512.png", type = "website", jsonLd, noindex = false, lang = "it", alternates = [] }) => {
  const fullTitle = title ? (title.includes(SITE) ? title : `${title} | ${SITE}`) : `${SITE} — Fiori a domicilio dal fiorista vicino a te`;
  const url = `${BASE}${path}`;
  const img = image.startsWith("http") ? image : `${BASE}${image}`;
  const ld = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <link rel="alternate" hrefLang={lang === "en" ? "en" : "it-IT"} href={url} />
      {alternates.map(a => <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={a.href.startsWith("http") ? a.href : `${BASE}${a.href}`} />)}
      {!alternates.some(a => a.hrefLang === "x-default") && <link rel="alternate" hrefLang="x-default" href={url} />}
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:site_name" content={SITE} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content={lang === "en" ? "en_US" : "it_IT"} />
      {lang === "en" && <meta property="og:locale:alternate" content="it_IT" />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={img} />
      {ld.map((obj, i) => <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>)}
    </Helmet>
  );
};

export const orgJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE,
  url: BASE,
  logo: `${BASE}/logo-full.png`,
  description: "Marketplace dei fioristi italiani: fiori a domicilio consegnati dal fiorista affiliato più vicino.",
  contactPoint: { "@type": "ContactPoint", telephone: "+39-339-995-1090", contactType: "customer service", areaServed: "IT", availableLanguage: "Italian" },
});

export const websiteJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE,
  url: BASE,
  potentialAction: { "@type": "SearchAction", target: `${BASE}/shops?q={search_term_string}`, "query-input": "required name=search_term_string" },
});

export const shopJsonLd = (shop) => ({
  "@context": "https://schema.org",
  "@type": "Florist",
  name: shop.name,
  description: shop.description,
  image: shop.logo_url || shop.cover_url || undefined,
  telephone: shop.phone || undefined,
  url: `${BASE}${shopPath(shop)}`,
  address: { "@type": "PostalAddress", streetAddress: shop.address, addressLocality: shop.city, addressCountry: "IT" },
  geo: shop.latitude ? { "@type": "GeoCoordinates", latitude: shop.latitude, longitude: shop.longitude } : undefined,
  aggregateRating: shop.review_count ? { "@type": "AggregateRating", ratingValue: shop.rating, reviewCount: shop.review_count } : undefined,
});

export const productJsonLd = (product) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: product.name,
  description: product.description,
  image: product.images || [],
  brand: { "@type": "Brand", name: product.shop_name },
  offers: {
    "@type": "Offer",
    priceCurrency: "EUR",
    price: (product.display_price ?? product.price).toFixed(2),
    availability: product.is_available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    url: `${BASE}/products/${product.id}`,
    seller: { "@type": "Organization", name: product.shop_name },
  },
});

export const faqJsonLd = (faq) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
});

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${BASE}${path}` })),
});
